import type { SortType } from './Sort.tsx'
import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/compat'
import { invoke } from 'apps/vtex/runtime.ts'

import { ProductListingPage } from 'apps/commerce/types.ts'

import ProductGallery from '$store/components/product/ProductGallery.tsx'
// import { LegacySort } from 'apps/vtex/utils/types.ts'

import { SealConfig } from '$store/loaders/seals.tsx'
import { TimerConfig } from '$store/loaders/timers.tsx'
import Spinner from 'site/components/ui/Spinner.tsx'
import { SelectedFacet } from 'apps/vtex/utils/types.ts'

export const getParamsFromURL = (params: URLSearchParams) => {
    const selectedFacets: SelectedFacet[] = []

    params.forEach((value, key) => {
        if (key.startsWith('filter.')) {
            selectedFacets.push({
                key: key.replace('filter.', ''),
                value,
            })
        }
    })

    // Sanitize `q` to handle '+' spaces and strip accidental data URI injections
    const raw = params.get('q') ?? undefined
    let query = raw ? raw.replace(/\+/g, ' ').trim() : undefined
    if (query && query.includes('data:text')) {
        query = query.split('data:text')[0]?.trim() || undefined
    }

    return { selectedFacets, query }
}

export interface Props {
    // id: string
    galleryId: string

    items?: number
    fetch?: 'next' | 'previous'
    pageInfo: ProductListingPage['pageInfo']
    sort?: string
    collectionId?: string

    seals: SealConfig[]
    timers: TimerConfig[]
    itemListName?: string
    itemListId?: string
    selectedSort: string | null
    showInfiniteScroll: boolean
    showMosaicoMobile: boolean
    device: string
    renderModalBuy?: boolean
    renderModalSimilars?: boolean
    hasBrand?: boolean
    pixDiscount?: number
    hidePixDiscountInShelf?: boolean
    fuzzy?: 'enabled'
    facets?: SelectedFacet[]
}

export default function InfiniteScroll({
    items = 0,
    pageInfo,
    sort,
    fetch = 'next',
    collectionId,
    seals,
    timers,
    itemListName,
    itemListId,
    selectedSort,
    showInfiniteScroll,
    showMosaicoMobile,
    device,
    galleryId,
    renderModalBuy,
    renderModalSimilars,
    pixDiscount,
    hidePixDiscountInShelf,
    fuzzy,
    facets,
}: Props) {
    const currentItems = useSignal(items)
    const nextPage = useSignal<ProductListingPage | null>(null)
    const total = pageInfo.records ?? 0
    // const sortE = useSort()

    const regexGetNumber = /.*page=(\d+).*/g
    useEffect(() => {
        if (pageInfo.previousPage) {
            const currentProducts =
                (Number(pageInfo.previousPage.replace(regexGetNumber, '$1')) ?? 1) *
                    (pageInfo.recordPerPage ?? 30) +
                items
            currentItems.value = currentProducts
        }
    }, [])

    const fetchNextPage = async () => {
        const currentUrl = new URL(globalThis.location.href)
        const searchParams = currentUrl.searchParams

        const offset = fetch === 'next' ? 0 : 2

        // Sanitize path segments to avoid forwarding injected data URIs or scripts
        const sanitizeSegment = (segment: string) => {
            const s = segment.trim()
            if (!s) return ''
            const lower = s.toLowerCase()
            if (lower.includes('data:') || lower.includes('javascript:')) return ''
            if (lower.includes('data%3a') || lower.includes('javascript%3a')) return ''
            if (s.length > 200) return ''
            return s
        }

        const sanitizedSegments = currentUrl.pathname
            .split('/')
            .map(sanitizeSegment)
            .filter((item) => item !== '')

        const selectedCategory = sanitizedSegments.map((item, index) => {
            return {
                key: `category-${index + 1}`,
                value: item,
            }
        })

        const { selectedFacets, query } = getParamsFromURL(searchParams)

        const isSearchPage = !!searchParams.get('q')
        const isBrandPage = facets?.some((facet) => facet.key === 'brand')

        let newSelectedFacets: SelectedFacet[] = [...selectedCategory, ...selectedFacets]

        if (facets) newSelectedFacets = [...facets, ...newSelectedFacets]

        if (collectionId) {
            newSelectedFacets = [
                {
                    key: `productClusterIds`,
                    value: collectionId,
                },
                ...selectedFacets,
            ]
        }

        if (isSearchPage) newSelectedFacets = selectedFacets
        if (isBrandPage) newSelectedFacets = facets ?? []

        const hasCategoryInFacets = facets?.some((facet) => facet.key.startsWith('category'))

        if (!hasCategoryInFacets) {
            newSelectedFacets = newSelectedFacets.filter(
                (facet) => !facet.key.startsWith('category'),
            )
        }

        try {
            const page = await invoke.vtex.loaders.intelligentSearch.productListingPage(
                {
                    selectedFacets: newSelectedFacets,
                    sort: sort as SortType,
                    count: pageInfo.recordPerPage,
                    query,
                    page: pageInfo.currentPage - offset,
                    fuzzy: fuzzy ?? 'disabled',
                    hideUnavailableItems: true,
                },
            )

            nextPage.value = page
        } catch (error) {
            const facetsStr = (newSelectedFacets ?? [])
                .map((f) => `${f.key}:${f.value}`)
                .join('|')
            nextPage.value = null
            const err = new Error(
                `InfiniteScroll: failed to fetch next page err=${
                    (error as Error)?.message ?? 'unknown'
                } facets=${facetsStr} sort=${sort ?? ''} count=${pageInfo.recordPerPage} query=${
                    query ?? ''
                } page=${pageInfo.currentPage - offset} fuzzy=${
                    fuzzy ?? 'disabled'
                } hideUnavailableItems=true`,
            )
            // Re-throw so the framework/section error boundary can capture and log it
            throw err
        }
    }

    if (nextPage.value && nextPage.value?.products.length > 0) {
        const $gallery = self?.document?.getElementById(galleryId)
        const grid = $gallery?.getAttribute('data-cols-layout')

        return (
            <ProductGallery
                products={nextPage.value.products}
                pageInfo={nextPage.value.pageInfo}
                fetch={fetch}
                collectionId={collectionId}
                seals={seals}
                sort={sort}
                timers={timers}
                itemListName={itemListName}
                itemListId={itemListId}
                selectedSort={selectedSort}
                showInfiniteScroll={showInfiniteScroll}
                showMosaicoMobile={showMosaicoMobile}
                device={device}
                gridCols={grid ?? ''}
                renderModalBuy={renderModalBuy}
                renderModalSimilars={renderModalSimilars}
                isMobile={device !== 'desktop'}
                pixDiscount={pixDiscount}
                hidePixDiscountInShelf={hidePixDiscountInShelf}
                fuzzy={fuzzy}
                facets={facets}
                preload={false}
            />
        )
    }
    const anchorLinkSEO = `${itemListName?.toLowerCase() ?? ''}${pageInfo?.nextPage ?? ''}`

    return (
        <>
            {
                /* <div
				data-previous={fetch === 'previous'}
				class={"grid w-full data-[previous='false']:grid-rows-2 grid-cols-1 lg:grid-rows-1 lg:grid-cols-3 mt-6 data-[previous='true']:mt-0 data-[previous='true']:mb-4"}
			> */
            }
            <div
                data-previous={fetch === 'previous'}
                class="flex justify-center items-center flex-col w-full gap-5 mt-2 data-[previous='true']:mt-0 "
            >
                {fetch === 'next' && (
                    <div class='flex flex-col items-center lg:items-center justify-center row-start-1  lg:col-start-3'>
                        <span class='text-sm text-black font-normal'>
                            Você está visualizando{' '}
                            <strong class='font-bold text-primary-700'>
                                {currentItems.value} produtos
                            </strong>{' '}
                            de{'  '}
                            <strong class='font-bold text-primary-700'>{total} produtos</strong>
                        </span>
                    </div>
                )}

                {pageInfo?.nextPage && (
                    <div class='row-start-2 lg:col-start-2 w-full flex justify-center items-center '>
                        <button
                            role='button'
                            aria-label='Botão para ver mais produtos'
                            type='button'
                            // data-loading
                            onClick={async (event) => {
                                const target = event.currentTarget as HTMLElement
                                target.setAttribute('data-loading', '')
                                try {
                                    await fetchNextPage()
                                    // deno-lint-ignore ban-ts-comment
                                    // @ts-ignore
                                    if (typeof konfidencyLoader !== 'undefined') {
                                        // deno-lint-ignore ban-ts-comment
                                        // @ts-ignore
                                        konfidencyLoader.loadShowcase()
                                    }
                                } finally {
                                    target.removeAttribute('data-loading')
                                }
                            }}
                            class='h-12 w-52 font-semibold bg-primary-700 hover:bg-primary-500 text-white transition-colors group '
                        >
                            <a
                                href={`/${anchorLinkSEO}`}
                                target='_self'
                                title={`Veja a ${
                                    pageInfo?.nextPage.match(/\d+/)
                                }ª página do nosso catalogo`}
                                class='pointer-events-none'
                                onClick={(e) => e.preventDefault()}
                            >
                                <div class='hidden group-data-[loading]:flex justify-center items-center gap-2'>
                                    <Spinner size={24} />
                                    <p>Carregando...</p>
                                </div>

                                <div class='flex group-data-[loading]:hidden justify-center items-center'>
                                    {fetch === 'next'
                                        ? '+ Ver mais produtos'
                                        : 'Ver produtos anteriores'}
                                </div>
                            </a>
                        </button>
                    </div>
                )}
            </div>
        </>
    )
}

// export function LoadingFallback() {
// 	return (
// 		<div class={'h-12 w-52 font-semibold bg-primary-700 hover:bg-primary-500 text-white transition-colors'}>
// 			<Spinner size={24} />
// 			<p>Carregando...</p>
// 		</div>
// 	)
// }
